import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { Box, Stack, useTheme, Radio, RadioGroup, FormControlLabel } from '@mui/material'

import TabPanel from 'components/global/TabPanel'
import TextField from 'components/global/CUK/TextField'
import ScrollableContent from 'packages/ui/ScrollableContent'
import { useUser } from 'store'

import { useSitesSelected, useTreeContext } from 'packages/ui/Arborist/TreeProvider'

import { toast } from 'components/global/CUK/Toast/Toast'

import { BaseChip } from './components'
import CategoryDefault, { CategoryContact, CategoryAdmin } from './Category'
import useCategories from './use-categories'
import ActionButtons from '../ActionButtons'

const RoleTooltip = ({ categories, role }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const category = categories.find(({ roles }) => Object.keys(roles).some(key => key === role))

  return (
    Boolean(role) && (
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          alignSelf: 'flex-start',
          width: '320px',
          p: 2,
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'grey.200',
          minHeight: '100px'
        }}>
        <Stack gap={1.5} sx={{ fontSize: 12 }}>
          <Stack gap={0.5}>
            <Stack sx={{ fontSize: 10, color: 'black' }}>{t(`auth.admin.role.category.${category?.name}`)}</Stack>
            <Stack sx={{ color: theme.palette.primary.main, fontSize: 14, fontWeight: 600 }}>{t(`auth.admin.role.${role}`)}</Stack>
          </Stack>
          <Stack sx={{color: theme.palette.grey[800]}}>
            {t(`auth.admin.role.tooltip.${role}`)}
          </Stack>
        </Stack>
      </Box>
    )
  )
}

const Filters = ({ categories, filters, onChange }) => {
  const { watch } = useFormContext()
  const roles = watch('roles')
  const { t } = useTranslation()
  const nbAll = categories.reduce((acc, cur) => acc + Object.keys(cur.roles).length, 0)
  const nbSelected = categories.reduce((acc, cur) => {
    return acc + Object.keys(cur.roles).filter(role => roles[role]).length
  }, 0)
  return (
    <Stack justifyContent={'space-between'} direction={'row'} gap={3}>
      <RadioGroup
        row
        value={filters.selected ? 'selected' : 'all'}
        onChange={e => {
          onChange({ selected: e.target.value !== 'all' })
        }}
        sx={{
          gap: 1,
          zIndex: 2,
          flexShrink: 0,
          flexWrap: 'nowrap',
          '& .MuiRadio-root': {
            padding: '4px',
            '& .MuiSvgIcon-root': {
              fontSize: '16px'
            }
          }
        }}>
        <FormControlLabel
          value="all"
          control={<Radio />}
          label={
            <Stack gap={1} sx={{ fontSize: '12px' }} direction={'row'} alignItems={'center'}>
              {t('auth.admin.roles_filter.all')}
              <BaseChip label={nbAll} />
            </Stack>
          }
        />
        <FormControlLabel
          value="selected"
          control={<Radio />}
          label={
            <Stack gap={1} sx={{ fontSize: '12px' }} direction={'row'} alignItems={'center'}>
              {t('auth.admin.roles_filter.selected', { count: nbSelected })}
              <BaseChip label={nbSelected} />
            </Stack>
          }
        />
      </RadioGroup>
      <TextField
        value={filters.verbatim}
        setvalue={newValue => {
          onChange({ verbatim: newValue })
        }}
        type="text"
        input={{
          placeholder: t('auth.admin.roles_filter.verbatim'),
          starticon: <i className={'fa-light fa-search'} />
        }}
      />
    </Stack>
  )
}
const Categories = ({ contact = null }) => {
  const { watch } = useFormContext()
  const roles = watch('roles')
  const [user] = useUser()
  const { t } = useTranslation()
  const categories = useCategories({ contact })
  const [filters, setFilters] = useState({ selected: false, verbatim: '' })
  const [roleHovered, setRoleHovered] = useState(null)

  const categoriesFiltred = useMemo(() => {
    return categories
      .map(category => {
        let newRoles = category.roles
        if (filters.selected) {
          newRoles = Object.keys(newRoles).reduce((acc, role) => {
            return {...acc, ...(roles[role] && {[role]: true})}
          }, {})
        }
        if (filters.verbatim.length) {
          newRoles = Object.keys(newRoles).reduce((acc, role) => {
            if (t(`auth.admin.role.${role}`).toLowerCase().indexOf(filters.verbatim.toLowerCase()) > -1) {
              return {...acc, [role]: true}
            }
            return acc
          }, {})
        }
        return {...category, roles: newRoles }
      })
      .filter(category => Object.keys(category.roles).length)
  }, [categories, filters, t])

  return (
    <Stack direction="row" flex={1} gap={3}>
      <Stack gap={3} sx={{ width: '560px' }}>
        <Filters
          categories={categories}
          filters={filters}
          onChange={newFilters => {
            setFilters(prev => ({ ...prev, ...newFilters }))
          }}
        />
        {
          user.admin && contact.internal && (
            <CategoryAdmin/>
          )
        }
        {
          !roles['admin']
            ? <>
                {categoriesFiltred.map(category => (
                  <CategoryDefault
                    key={category.id}
                    category={category}
                    contact={contact}
                    onRoleMouseEnter={role => setRoleHovered(role)}
                    onRoleMouseLeave={() => setRoleHovered(null)}
                  />
                ))}
                {
                  filters.verbatim.length === 0 && !filters.selected && (
                    <CategoryContact />
                  )
                }
              </>
          : <Stack sx={{fontSize: 12}}>{t('auth.admin.roles.disclaimer_admin')}</Stack>
        }

        <Stack sx={{ height: 20 }} />
      </Stack>

      <RoleTooltip categories={categories} role={roleHovered} />
    </Stack>
  )
}

const Roles = ({ loading, contact, isLocked, isInternal, onSubmit, onGoBack }) => {
  const {
    formState: { errors },
    watch,
    setValue,
    getValues
  } = useFormContext()
  const { selection } = useTreeContext()
  const { count: nbSitesSelected } = useSitesSelected({ selection })
  const [user] = useUser()
  const disabled = useMemo(() => {
    if (user.admin) {
      return false
    }
    return !user.roles.admin_roles
  }, [user])
  const { t } = useTranslation()
  const sfId = watch('sf_id')

  const handleSubmit = async () => {
    if (sfId && Object.entries(errors).length > 0) {
      toast.error(t('auth.admin.edit.form_errors'))
      return
    }
    if (!isInternal && sfId && nbSitesSelected === 0) {
      toast.error(t('auth.admin.edit.sites_selected_errors'))
      return
    }
    const {
      roles: { admin = false }
    } = getValues()
    setValue('admin', admin)

    onSubmit()
  }

  return (
    <TabPanel value={'settings'} index="settings">
      <Stack
        sx={{
          height: 'calc(100vh - 215px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
        <ScrollableContent noHorizontalScroll>
          <Categories isInternal={isInternal} contact={contact} withBorder isRoleEdit />
        </ScrollableContent>
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'white',
            borderTop: '1px solid #eee'
          }}>
          <ActionButtons
            disabled={disabled || isLocked}
            loading={loading}
            onSubmit={handleSubmit}
            onGoBack={onGoBack}
            labelSubmit={sfId ? t('ui_elements.form_validate') : t('ui_elements.form_next')}
          />
        </Box>
      </Stack>
    </TabPanel>
  )
}

export default Roles
